import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,

} from '@mui/material';
import { Save, CheckCircle, Cancel, ThumbUp, ThumbDown } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useParams, useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { LeaveType, LeaveStatus, Leave } from '../types';
import Layout from '../components/Layout/Layout';
import { useAuth } from '../contexts/AuthContext';

const LeaveForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [leave, setLeave] = useState<Leave | null>(null);
  const [formData, setFormData] = useState({
    type: LeaveType.FULL_DAY,
    startDate: new Date(),
    endDate: new Date(),
    hours: 0,
    reason: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [rejectionDialog, setRejectionDialog] = useState(false);
  const [approvalType, setApprovalType] = useState<'approve' | 'reject'>('approve');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (id) {
      loadLeave();
    }
  }, [id]);

  const loadLeave = async () => {
    try {
      setLoading(true);
      const data = await apiService.getLeaveById(id!);
      setLeave(data);
      setFormData({
        type: data.type,
        startDate: new Date(data.startDate),
        endDate: new Date(data.endDate),
        hours: data.hours || 0,
        reason: data.reason,
      });
    } catch (error: any) {
      console.error('Error loading leave:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error?.message || 
                          error.message || 
                          'Failed to load leave request';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);

      const submitData = {
        type: formData.type,
        startDate: formData.startDate.toISOString().split('T')[0],
        endDate: formData.endDate.toISOString().split('T')[0],
        hours: formData.type === LeaveType.HOURS ? formData.hours : undefined,
        reason: formData.reason,
      };

      if (id) {
        await apiService.updateLeave(id, submitData);
      } else {
        await apiService.createLeave(submitData);
      }

      navigate('/leaves');
    } catch (error: any) {
      console.error('Error saving leave:', error);
      // Extract error message from the response
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error?.message || 
                          error.message || 
                          'Failed to save leave request';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const canEdit = !leave || leave.status === LeaveStatus.PENDING || leave.status === LeaveStatus.REJECTED;

  // Check if current user can approve/reject this leave
  const canApproveReject = user && (user.role === 'admin' || user.role === 'manager' || 
                          user.role === 'super_manager' || user.role === 'hr_admin' || 
                          user.role === 'root_user') && leave && leave.status === LeaveStatus.PENDING;

  const openApprovalDialog = () => {
    setApprovalType('approve');
    setApprovalDialog(true);
  };

  const openRejectionDialog = () => {
    setApprovalType('reject');
    setRejectionDialog(true);
  };

  const handleApproval = async () => {
    if (!leave) return;

    try {
      setLoading(true);
      setError(null);

      if (approvalType === 'approve') {
        await apiService.approveLeave(leave.id);
        setSuccess('Leave request approved successfully!');
      } else {
        await apiService.rejectLeave(leave.id, reason);
        setSuccess('Leave request rejected successfully!');
      }

      // Clear form
      setReason('');
      setApprovalDialog(false);
      setRejectionDialog(false);

      // Reload leave data
      await loadLeave();

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error: any) {
      console.error('Failed to process leave request:', error);
      const errorMessage = error.response?.data?.message || 
                          error.response?.data?.error?.message || 
                          error.message || 
                          'Failed to process leave request';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: LeaveStatus) => {
    switch (status) {
      case LeaveStatus.APPROVED:
        return 'success';
      case LeaveStatus.REJECTED:
        return 'error';
      case LeaveStatus.PENDING:
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading && !leave && id) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">
          {id ? 'Leave Request Details' : 'New Leave Request'}
        </Typography>
        {leave && (
          <Box sx={{ mt: 1 }}>
            <Chip
              label={leave.status}
              color={getStatusColor(leave.status)}
              size="small"
            />
          </Box>
        )}
      </Box>

              {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

      {leave && leave.status === LeaveStatus.REJECTED && leave.rejectionReason && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>Rejection Reason:</Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {leave.rejectionReason}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You can modify this request and resubmit it for approval.
          </Typography>
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Leave Type"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as LeaveType })}
                disabled={!canEdit}
                required
              >
                <MenuItem value={LeaveType.FULL_DAY}>Full Day</MenuItem>
                <MenuItem value={LeaveType.HALF_DAY}>Half Day</MenuItem>
                <MenuItem value={LeaveType.HOURS}>Hours</MenuItem>
              </TextField>
            </Grid>

            {formData.type === LeaveType.HOURS && (
              <Grid item xs={12} sm={6}>
                <TextField
                  type="number"
                  fullWidth
                  label="Hours"
                  value={formData.hours}
                  onChange={(e) => setFormData({ ...formData, hours: parseFloat(e.target.value) || 0 })}
                  inputProps={{ min: 0.5, max: 8, step: 0.5 }}
                  disabled={!canEdit}
                  required
                />
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Start Date"
                value={formData.startDate}
                onChange={(date) => date && setFormData({ ...formData, startDate: date, endDate: date })}
                disabled={!canEdit}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <DatePicker
                label="End Date"
                value={formData.endDate}
                onChange={(date) => date && setFormData({ ...formData, endDate: date })}
                disabled={!canEdit || formData.type === LeaveType.HALF_DAY || formData.type === LeaveType.HOURS}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Reason"
                value={formData.reason}
                onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                disabled={!canEdit}
                required
                placeholder="Please provide a reason for your leave request..."
              />
            </Grid>

            {leave && leave.approver && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">
                  {leave.status === LeaveStatus.APPROVED ? 'Approved by:' : 
                   leave.status === LeaveStatus.REJECTED ? 'Rejected by:' : 'Reviewed by:'}
                </Typography>
                <Typography variant="body2">
                  {leave.approver.fullName}
                  {leave.approvedAt && ` on ${new Date(leave.approvedAt).toLocaleDateString()}`}
                </Typography>
              </Grid>
            )}
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              onClick={() => navigate('/leaves')}
            >
              {canEdit ? 'Cancel' : 'Back'}
            </Button>
            {canEdit && (
              <Button
                type="submit"
                variant="contained"
                startIcon={<Save />}
                disabled={loading}
              >
                {id ? (leave?.status === LeaveStatus.REJECTED ? 'Resubmit' : 'Update') : 'Submit'} Request
              </Button>
            )}
            {canApproveReject && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ThumbUp />}
                  onClick={openApprovalDialog}
                  disabled={loading}
                >
                  Approve
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<ThumbDown />}
                  onClick={openRejectionDialog}
                  disabled={loading}
                >
                  Reject
                </Button>
              </>
            )}
          </Box>
        </form>
      </Paper>

      {/* Approval Dialog */}
      <Dialog open={approvalDialog} onClose={() => setApprovalDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Approve Leave Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Are you sure you want to approve this leave request?
          </Typography>
          {leave && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Employee:</strong> {leave.user?.firstName} {leave.user?.lastName}
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {leave.type === LeaveType.FULL_DAY ? 'Full Day' : 
                                      leave.type === LeaveType.HALF_DAY ? 'Half Day' : 'Hours'}
              </Typography>
              <Typography variant="body2">
                <strong>Dates:</strong> {new Date(leave.startDate).toLocaleDateString()} - {new Date(leave.endDate).toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                <strong>Reason:</strong> {leave.reason}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
          <Button
            onClick={handleApproval}
            variant="contained"
            color="success"
            startIcon={<CheckCircle />}
            disabled={loading}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={rejectionDialog} onClose={() => setRejectionDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Leave Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Please provide a reason for rejecting this leave request.
          </Typography>
          {leave && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
              <Typography variant="body2">
                <strong>Employee:</strong> {leave.user?.firstName} {leave.user?.lastName}
              </Typography>
              <Typography variant="body2">
                <strong>Type:</strong> {leave.type === LeaveType.FULL_DAY ? 'Full Day' : 
                                      leave.type === LeaveType.HALF_DAY ? 'Half Day' : 'Hours'}
              </Typography>
              <Typography variant="body2">
                <strong>Dates:</strong> {new Date(leave.startDate).toLocaleDateString()} - {new Date(leave.endDate).toLocaleDateString()}
              </Typography>
              <Typography variant="body2">
                <strong>Reason:</strong> {leave.reason}
              </Typography>
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Please provide a reason for rejection..."
            sx={{ mt: 2 }}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectionDialog(false)}>Cancel</Button>
          <Button
            onClick={handleApproval}
            variant="contained"
            color="error"
            startIcon={<Cancel />}
            disabled={loading || !reason.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default LeaveForm;